import { ExpressionRunner } from "survey-core";

/**
 * Evaluates all defaultValueExpression fields in the survey
 * and ensures they are stored in the survey data, even if not visible or readonly
 * @param {Survey} survey - The SurveyJS instance
 */
export default function evaluateDefaultExpressions(survey) {
  if (!survey || !survey.toJSON) {
    return;
  }

  const surveyJson = survey.toJSON();
  const currentData = survey.data;

  surveyJson.pages.forEach(page => {
    if (page.elements) {
      page.elements.forEach(element => {
        processElement(element, survey, currentData);
      });
    }
  });
}

/**
 * Recursively processes an element and its nested elements
 * @param {Object} element - The element to process
 * @param {Survey} survey - The SurveyJS instance
 * @param {Object} currentData - Current survey data
 */
function processElement(element, survey, currentData) {
  // Check if element should have defaultValueExpression or defaultValue evaluated
  if (shouldEvaluateDefault(element, currentData)) {
    evaluateAndSetValue(element, survey, currentData);
  }

  // Process nested elements (for panels, dynamic panels, etc.)
  if (element.elements) {
    element.elements.forEach(nestedElement => {
      processElement(nestedElement, survey, currentData);
    });
  }

  // Process template elements (for dynamic panels)
  if (element.templateElements) {
    element.templateElements.forEach(templateElement => {
      processElement(templateElement, survey, currentData);
    });
  }
}

/**
 * Checks if an element should have its default value evaluated
 * @param {Object} element - The element to check
 * @returns {boolean}
 */
function shouldEvaluateDefault(element) {
  // Must have a name
  if (!element.name) {
    return false;
  }

  // Must have defaultValueExpression or defaultValue
  if (!element.defaultValueExpression && !element.defaultValue) {
    return false;
  }

  // Only evaluate if:
  // - visible is false OR readOnly is true
  const isHidden = element.visible === false;
  const isReadOnly = element.readOnly === true;

  return isHidden || isReadOnly;
}

/**
 * Evaluates and sets the default value for an element
 * @param {Object} element - The element to evaluate
 * @param {Survey} survey - The SurveyJS instance
 * @param {Object} currentData - Current survey data
 */
function evaluateAndSetValue(element, survey, currentData) {
  const questionName = element.name;

  try {
    const question = survey.getQuestionByName(questionName);
    
    if (question) {
      // If there's a defaultValueExpression, evaluate it
      if (element.defaultValueExpression) {
        // Use SurveyJS's expression runner to evaluate the expression
        const expression = new ExpressionRunner(element.defaultValueExpression);
        const evaluatedValue = expression.run(currentData);
        
        if (evaluatedValue !== undefined && evaluatedValue !== null && evaluatedValue !== "") {
          survey.setValue(questionName, evaluatedValue);
        }
      } 
      // Otherwise use defaultValue
      else if (element.defaultValue !== undefined) {
        survey.setValue(questionName, element.defaultValue);
      }
    } else {
      // If question doesn't exist in survey (might be truly hidden), evaluate expression directly
      if (element.defaultValueExpression) {
        const expression = new ExpressionRunner(element.defaultValueExpression);
        const evaluatedValue = expression.run(currentData);
        
        if (evaluatedValue !== undefined && evaluatedValue !== null && evaluatedValue !== "") {
          survey.setValue(questionName, evaluatedValue);
        }
      } else if (element.defaultValue !== undefined) {
        survey.setValue(questionName, element.defaultValue);
      }
    }
  } catch (error) {
    console.warn(`Failed to evaluate default for ${questionName}:`, error);
  }
}
